import React, {
  useCallback,
  useState,
  ChangeEvent,
  ComponentProps,
  ReactElement,
} from 'react';

import PropTypes from 'prop-types';

import Avatar, { DefaultAvatar } from '@adsk/alloy-react-avatar';
import Button, { LinkButton } from '@adsk/alloy-react-button';
import { useControlled } from '@adsk/alloy-react-helpers';
import { AlertWarningFilledIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import DefaultCommentInput from './layout/DefaultCommentInput';

const defaultRenderInput = (
  props: ComponentProps<typeof DefaultCommentInput>,
) => <DefaultCommentInput {...props} />;

export type CommentFieldProps = Omit<
  StylableComponent<HTMLDivElement>,
  'onSubmit' | 'onChange'
> & {
  user: { name: string; defaultAvatar?: boolean; imageUrl?: string | null };
  value?: string;
  submitting?: boolean;
  error?: string;
  maxLength?: number;
  onChange?: (value: string) => void;
  onSubmit: (value: string) => void;
  onCancel?: () => void;
  renderInput?: (
    props: ComponentProps<typeof DefaultCommentInput>,
  ) => ReactElement | null;
  softLimit?: number;
  showCountThreshold?: number;
};

/**
 * A Comment field component showing user avatar, a text input, and form buttons
 */
const CommentField = React.forwardRef<HTMLDivElement, CommentFieldProps>(
  (
    {
      style,
      className,
      value: inputValue,
      user,
      submitting,
      error,
      maxLength,
      onChange,
      onCancel,
      onSubmit,
      renderInput = defaultRenderInput,
      softLimit,
      showCountThreshold,
    },
    ref,
  ) => {
    const [focused, setFocused] = useState<boolean | undefined>(false);
    const [value, setValue] = useControlled('', inputValue as string, onChange);
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

    const handleSubmit = useCallback(() => {
      if (value) {
        onSubmit(value);
      }
    }, [value, onSubmit]);

    const handleCancelClick = useCallback(() => {
      onCancel && onCancel();
    }, [onCancel]);

    const handleBlur = useCallback(
      (event?: ChangeEvent) => {
        if (!value && !error) {
          event?.preventDefault();
          setFocused(undefined);
        }
      },
      [error, value],
    );

    return (
      <div css={[{ display: 'flex' }, style]} className={className} ref={ref}>
        {user.defaultAvatar ? (
          <DefaultAvatar />
        ) : (
          <Avatar
            imageUrl={user.imageUrl}
            name={user ? user.name : undefined}
          />
        )}
        <div style={{ flex: 1, marginLeft: '14px' }}>
          {renderInput({
            value,
            focused,
            error,
            maxLength,
            softLimit,
            showCountThreshold,
            disabled: submitting,
            onBlur: handleBlur,
            onFocus: () => setFocused(true),
            onChange: (newValue: string) => setValue(newValue),
            style: {
              width: '100%',
              minHeight: 68,
              color: theme.tokens.colors.text.primary.value,
            },
          })}

          {(focused || error || submitting) && (
            <div
              style={{
                marginTop: error ? '5px' : '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: error ? 'flex-start' : 'flex-end',
              }}
            >
              {error && (
                <div
                  style={{
                    ...theme.typography.bodySmall,
                    color: theme.tokens.colors.text.error.value,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <AlertWarningFilledIcon
                    size={16}
                    style={{
                      color: theme.tokens.colors.icon.error.value,
                      marginRight: theme.spacing.XS,
                    }}
                  />
                  <span>{error}</span>
                </div>
              )}
              {!error && (
                <div>
                  <LinkButton
                    disabled={!value || submitting}
                    onClick={handleCancelClick}
                    size={Button.SIZES.SMALL}
                    style={{ marginRight: theme.spacing.S }}
                    data-testid="CommentField__cancel"
                  >
                    {T.translate('CANCEL')}
                  </LinkButton>
                  <Button
                    variant={Button.VARIANTS.PRIMARY}
                    disabled={!value}
                    loading={submitting}
                    size={Button.SIZES.SMALL}
                    style={
                      submitting
                        ? {
                            backgroundColor:
                              theme.tokens.colors.background.brand.inactive
                                .value,
                          }
                        : {}
                    }
                    onClick={handleSubmit}
                    data-testid="CommentField__submit"
                  >
                    {T.translate('SUBMIT')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);
CommentField.displayName = 'CommentField';
CommentField.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value of the comment field */
  value: PropTypes.string,
  /** The current user object */
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
  }).isRequired,
  /** Is submitting comment **/
  submitting: PropTypes.bool,
  /** An error message. When set triggers error state and shows it in a label **/
  error: PropTypes.string,
  /** Max characters allowed (activates character count) */
  maxLength: PropTypes.number,
  /* Triggers when comment input field is changed **/
  onChange: PropTypes.func,
  /* Triggers when submit button is clicked **/
  onSubmit: PropTypes.func.isRequired,
  /** Triggers when clear button is clicked **/
  onCancel: PropTypes.func,
  /** Render custom input component. Props ({ onBlur, onChange, onFocus, disabled, error, value }) */
  renderInput: PropTypes.func,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length */
  showCountThreshold: PropTypes.number,
};
export default CommentField;
